import { SubmissionStatus } from '@bp/bp-graphql-types';
import { Button, EmptyState, Input, LazyLoader, ListIcon, SendIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { UpdateSubmissionsMutation, useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { useActivityProtocol } from 'hooks/matrix/useActivityProtocol';
import { useGetMatrixRoomByName } from 'hooks/matrix/useGetMatrixRoomByName';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { EventType } from 'matrix-js-sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { NotificationReason, RoomNames } from 'utils/matrixClient';
import { showUserDbErrorToast } from 'utils/toasts';
import { useRoomTimeline } from '../../matrix/hooks/useRoomTimeline';
import styles from './ActivityProtocol.module.scss';
import { ActivityProtocolEntry } from './ActivityProtocolEntry/ActivityProtocolEntry';

type ActivityProtocolProps = {
  submissionUuid: string;
};

export const ActivityProtocol = ({ submissionUuid }: ActivityProtocolProps) => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);

  const [, updateSubmissionMutation] = useMutation(UpdateSubmissionsMutation);

  const permissions = usePermissionChecker();

  const { isAvailable: matrixAvailable } = useMatrixClient();
  const { getRoom } = useGetMatrixRoomByName();
  const { sendMessage, redactMessage } = useActivityProtocol();

  const [loading, setLoading] = useState<boolean>(false);
  const [comment, setComment] = useState<string | null>(null);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid, submissionUuid });

  const submission = submissions.find((s) => s.uuid === submissionUuid);
  if (!submission) throw new Error('Submission not found');

  const assignment = submission.assignment;
  const room = getRoom(submissionUuid, RoomNames.Submission);

  const messages = useRoomTimeline(room?.roomId ?? '').filter((m) => m.getType() === EventType.RoomMessage);

  async function handleSendComment() {
    if (!room || !comment) return;
    setLoading(true);
    await sendMessage(submissionUuid, NotificationReason.SubmissionComment, comment);
    setComment(null);
    setLoading(false);
  }

  async function handleGrandFeedbackRequest(eventId: string) {
    if (!room) return;
    const { error } = await updateSubmissionMutation({
      where: {
        uuid: submissionUuid,
      },
      update: {
        status: SubmissionStatus.Feedback,
      },
    });
    if (error) {
      showUserDbErrorToast();
      return;
    }
    const { ok } = await redactMessage(room.roomId, eventId);
    if (ok) {
      await sendMessage(submissionUuid, NotificationReason.SubmissionFeedback);
    } else {
      showUserDbErrorToast();
    }
  }

  async function rejectGrandFeedbackRequest(eventId: string) {
    if (!room) return;
    const { ok } = await redactMessage(room.roomId, eventId);
    if (ok) {
      await sendMessage(submissionUuid, NotificationReason.SubmissionFeedbackRejected);
    } else {
      showUserDbErrorToast();
    }
  }

  async function handlePublishSubmission(eventId: string, isPublic: boolean) {
    if (!room) return;
    const { error } = await updateSubmissionMutation({
      where: {
        uuid: submissionUuid,
      },
      update: {
        public: isPublic,
      },
    });
    if (error) {
      showUserDbErrorToast();
      return;
    }
    const { ok } = await redactMessage(room.roomId, eventId);
    if (ok) {
      await sendMessage(
        submissionUuid,
        isPublic ? NotificationReason.SubmissionPublished : NotificationReason.SubmissionDePublished,
        null,
        true,
      );
    } else {
      showUserDbErrorToast();
    }
  }

  const isTeacher = permissions.canViewSubmissions({
    uuid: assignment.uuid,
    ownerUuid: submission.uuid,
    groupUuid: assignment.course.uuid,
    organization: { uuid: assignment.course.organizationUuid },
  });

  return (
    <div className={styles['activity-protocol']}>
      {!matrixAvailable ? (
        <LazyLoader embedded />
      ) : messages.length === 0 ? (
        <EmptyState
          size='small'
          padding='none'
          title={t('activityProtocol.noActivity')}
          icon={<ListIcon />}
          className='pt-1 pr-5 pb-6 pl-5'
        />
      ) : (
        <div className={styles.protocol}>
          {messages.map((m, index) => (
            <ActivityProtocolEntry
              key={m.getId()}
              event={m}
              isTeacher={isTeacher}
              className={classNames(styles.entry, {
                [styles.first]: index === 0,
                [styles.last]: index === messages.length - 1,
              })}
              onGrantFeedbackRequest={(eventId) => handleGrandFeedbackRequest(eventId)}
              onRejectFeedbackRequest={(eventId) => rejectGrandFeedbackRequest(eventId)}
              onPublishSubmission={(eventId, isPublic) => handlePublishSubmission(eventId, isPublic)}
            />
          ))}
        </div>
      )}
      <Input
        dense
        name='comment'
        placeholder={t('activityProtocol.askQuestion')}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !loading) handleSendComment();
        }}
        value={comment}
        suffix={
          <Button hierarchy='ghost' icon={<SendIcon />} onClick={() => handleSendComment()} isLoading={loading} />
        }
        className={styles.input}
      />
    </div>
  );
};
